/* eslint-disable react/jsx-indent */
import React, {
  useEffect, useReducer, useState,
} from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import {
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { HelmetProvider } from 'react-helmet-async';
import { theme } from 'shared/theme';
import AnalyticsService from 'services/analytics';
import AuthService from 'services/auth';
import NationalService from 'services/national';
import PersonService from 'services/person';
import Login from 'pages/Login';
import AddAdmin from 'pages/AddAdmin';
import {
  APP_AUTH_STATES,
  authReducer,
  initialAuthState,
  UserContext,
} from 'shared/contexts/UserContext';
import AppLoading from 'components/AppLoading';
import AppRouting from 'components/AppRouting';
import GlobalStyles from 'components/GlobalStyles';
import useDidMountEffect from 'shared/hooks/useDidMountEffect';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import NotificationContext from 'shared/contexts/NotificationContext';
import AlertContext from 'shared/contexts/AlertContext';
import { Company, Role } from 'API';
import SidebarContext from 'shared/contexts/SidebarContext';
import { LoginStage } from 'shared/enums';

const App: React.FC = () => {
  const [userState, userDispatch] = useReducer(authReducer, initialAuthState);
  const [hasCheckedForAuth, setHasCheckedForAuth] = useState(false);
  const { person, role, user } = userState;
  const [
    updatedNotificationCount, setUpdatedNotificationCount,
  ] = useState<number | undefined>();
  const [alert, setAlert] = useState<Snack | null>(null);
  const [newItem, setNewItem] = useState<Person | Company>();
  const [updatedItem, setUpdatedItem] = useState<Person | Company>();
  const isEmailVerified = localStorage.getItem('isEmailVerified');

  useEffect(() => {
    // Check to see if user is logged in. If so, use userDispatch to store
    // user data in userState.
    getCurrentUser()
      .then(async (authData) => {
        const thisPerson = await PersonService.getCurrent();
        const thisRole = await AuthService.getRoleForCurrentUser();
        const { accessCompanies, adminCompanies } = await AuthService
          .getCompaniesCurrentUserCanAccess();
        const dependents = thisRole === Role.FOREIGNNATIONAL
          ? NationalService.getDependentIDs(thisPerson) : [];

        if (thisPerson) {
          PersonService.setPersonID(thisPerson.id);
          PersonService.setPersonType(thisPerson.__typename);
          PersonService.setPerson(thisPerson);
        }

        if (authData) {
          userDispatch({
            type: 'login',
            authState: APP_AUTH_STATES.SignedIn,
            authData,
            person: thisPerson,
            role: thisRole,
            adminCompanies,
            accessCompanies,
            dependents,
          });
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
      })
      .finally(() => {
        setHasCheckedForAuth(true);
      });

    AnalyticsService.initialize();
  }, []);

  useDidMountEffect(() => {
    if (userState.authState === APP_AUTH_STATES.SignedIn && person) {
      AnalyticsService.registerUser(person, role);
    }

    AnalyticsService.logAuthStatechange(userState.authState);
  }, [userState.authState]);

  if (!hasCheckedForAuth) {
    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <AppLoading />
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }

  return (
    <HelmetProvider>
      <UserContext.Provider value={[userState, userDispatch]}>
        <SidebarContext.Provider
          value={{
            newItem, setNewItem, updatedItem, setUpdatedItem,
          }}
        >
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <NotificationContext.Provider value={{
                updatedCount: updatedNotificationCount,
                setUpdatedCount: setUpdatedNotificationCount,
              }}
              >
                <AlertContext.Provider value={[alert, setAlert]}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <CssBaseline />
                    <GlobalStyles />
                    {userState.authState === APP_AUTH_STATES.SignedIn
                      && !(isEmailVerified === LoginStage.IS_EMAIL_VERIFIED.toString()) ? (
                        person ? (
                          <AppRouting />
                        ) : (
                          <AddAdmin />
                        )
                      ) : (
                        <div className="page-grid">
                          <Login />
                        </div>
                      )}
                  </LocalizationProvider>
                </AlertContext.Provider>
              </NotificationContext.Provider>
            </ThemeProvider>
          </StyledEngineProvider>
        </SidebarContext.Provider>
      </UserContext.Provider>
    </HelmetProvider>
  );
};

export default App;
